/**
 * Таблица с чекбоксами для массового выбора строк
 * Дата: 29.01.2026
 * Назначение: UI компонент для выбора пациентов в массовой привязке
 * Связан с: TODO-021a (mass_binding_enhancements)
 */
import { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from './Table';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

interface CheckboxTableProps<T> {
  data: T[];
  columns: Column<T>[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  getRowId: (row: T) => string;
  isRowDisabled?: (row: T) => boolean;
  className?: string;
  testId?: string;
}

export function CheckboxTable<T>({ 
  data, 
  columns, 
  selectedIds, 
  onSelectionChange, 
  getRowId,
  isRowDisabled,
  className,
  testId
}: CheckboxTableProps<T>) {
  const allSelected = data.length > 0 && data.every(row => {
    const id = getRowId(row);
    return isRowDisabled?.(row) || selectedIds.has(id);
  });
  
  const someSelected = data.some(row => {
    const id = getRowId(row);
    return !isRowDisabled?.(row) && selectedIds.has(id);
  });

  const handleSelectAll = () => {
    if (allSelected) {
      // Снять выделение со всех строк из текущих данных
      const newSelection = new Set(selectedIds);
      data.forEach(row => {
        if (!isRowDisabled?.(row)) {
          newSelection.delete(getRowId(row));
        }
      });
      onSelectionChange(newSelection);
    } else {
      // Выделить все доступные строки
      const newSelection = new Set(selectedIds);
      data.forEach(row => {
        if (!isRowDisabled?.(row)) {
          newSelection.add(getRowId(row));
        }
      });
      onSelectionChange(newSelection);
    }
  };
  
  const handleRowSelect = (rowId: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(rowId)) {
      newSelection.delete(rowId);
    } else {
      newSelection.add(rowId);
    }
    onSelectionChange(newSelection);
  };
  
  return (
    <div className={cn('w-full', className)} data-testid={testId}>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-12">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected && !allSelected;
                }}
                onChange={handleSelectAll}
                className="w-4 h-4 text-shell-primary rounded border-shell-border focus:ring-shell-primary/40"
              />
            </TableHeader>
            {columns.map(col => (
              <TableHeader key={col.key} className={col.className}>
                {col.header}
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(row => {
            const rowId = getRowId(row);
            const disabled = isRowDisabled?.(row);
            const isSelected = selectedIds.has(rowId);
            
            return (
              <TableRow 
                key={rowId} 
                className={cn(
                  disabled && 'opacity-50 cursor-not-allowed',
                  isSelected && 'bg-shell-primary-soft'
                )}
              >
                <TableCell>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleRowSelect(rowId)}
                    disabled={disabled}
                    className="w-4 h-4 text-shell-primary rounded border-shell-border focus:ring-shell-primary/40 disabled:opacity-50"
                  />
                </TableCell>
                {columns.map(col => (
                  <TableCell key={col.key} className={col.className}>
                    {col.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
